// ButtonMicro.tsx
import React, { FunctionComponent, useState, useEffect } from "react";
import styled from "styled-components";
import Icon from "../global/Icon";
import Colors, { blendWithWhite } from "../global/Colors";
import Fonts from "../global/Fonts";

const ButtonContainer = styled.button<{ 
  $colorMode: 'light' | 'dark' | 'red',
  $isHovered: boolean,
  $isActive: boolean,
  $disabled: boolean,
  $padding: string,
  $borderRadius: string,
  $showBorder: boolean,
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: ${props => props.$borderRadius};
  padding: ${props => props.$padding};
  border: ${props => props.$showBorder ? '1px solid' : 'none'} ${props => {
    if (props.$disabled) {
      if (props.$colorMode === 'red') return blendWithWhite(Colors.red, 0.4);
      return props.$colorMode === 'dark' ? Colors.grey8+80 : Colors.grey1+80;
    }
    if (props.$isHovered) {
      if (props.$colorMode === 'red') return Colors.redLight;
      return props.$colorMode === 'dark' ? Colors.grey7 : Colors.grey2;
    }
    if (props.$colorMode === 'red') return Colors.red;
    return props.$colorMode === 'dark' ? Colors.grey8 : Colors.grey1;
  }};
  background-color: ${props => {
    if (props.$disabled) {
      if (props.$colorMode === 'red') return blendWithWhite(Colors.red, 0.4);
      return Colors.transparent;
    }
    if (props.$isActive) {
      if (props.$colorMode === 'red') return Colors.redDark;
      return props.$colorMode === 'dark' ? Colors.black : Colors.grey2;
    }
    if (props.$isHovered) {
      if (props.$colorMode === 'red') return Colors.redLight;
      return props.$colorMode === 'dark' ? Colors.grey8 : Colors.grey1;
    }
    if (props.$colorMode === 'red') return Colors.red;
    return props.$colorMode === 'dark' ? Colors.grey9 : Colors.grey1;
  }};
  cursor: ${props => props.$disabled ? 'default' : 'pointer'};
  transition: background-color 0.2s ease, border-color 0.2s ease;
  
  &:focus {
    outline: none;
  }
`;

const Text = styled.span<{ $colorMode: 'light' | 'dark' | 'red' }>`
  color: ${props => props.$colorMode === 'red' ? Colors.white : props.$colorMode === 'dark' ? Colors.white : Colors.grey8};
  font-family: ${Fonts.lexendLight.fontFamily};
  font-weight: ${Fonts.lexendLight.fontWeight};
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 4px;
`;

export interface ButtonMicroProps {
  icon?: string;
  text?: string;
  onClick: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  colorMode?: 'light' | 'dark' | 'red';
  size?: number;
  borderRadius?: string;
  padding?: string;
  showBorder?: boolean;
}

const ButtonMicro: FunctionComponent<ButtonMicroProps> = ({
  icon,
  text,
  onClick,
  disabled = false,
  colorMode = 'dark',
  size = 16,
  borderRadius = '4px',
  padding = '4px 0px',
  showBorder = true,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (disabled) {
      setIsHovered(false);
      setIsActive(false);
    }
  }, [disabled]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      onClick(e);
    }
  };

  const iconColor = disabled 
    ? (colorMode === 'red' ? blendWithWhite(Colors.white, 0.4) : colorMode === 'dark' ? Colors.grey6 : Colors.grey4)
    : (colorMode === 'red' ? Colors.white : colorMode === 'dark' ? Colors.white : Colors.grey8);

  return (
    <ButtonContainer
      $colorMode={colorMode}
      $isHovered={isHovered}
      $isActive={isActive}
      $disabled={disabled}
      $padding={padding}
      $borderRadius={borderRadius}
      $showBorder={showBorder}
      onClick={handleClick}
      onMouseEnter={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); setIsActive(false); }}
      onMouseDown={() => !disabled && setIsActive(true)}
      onMouseUp={() => !disabled && setIsActive(false)}
      onTouchStart={() => !disabled && setIsActive(true)}
      onTouchEnd={() => !disabled && setIsActive(false)}
      disabled={disabled}
    >
      {text && <Text $colorMode={colorMode}>{text}</Text>}
      {icon && <Icon icon={icon} color={iconColor} size={size} />}
    </ButtonContainer>
  );
};

export default ButtonMicro;